import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Btn, DataTable, KPI, KPIRow, Loading, Msg, Section, fmtDate, panelGet, panelPost, useAdminGet,
} from './kit';
import { Modal } from './email/emailKit';
import { Pill, type Tone } from './ipintel/shared';
import { AdminLink } from './adminNav';
import { useAdminAccess } from './adminAccess';

/**
 * System → Search indexes (owner, 2026-10-11).
 *
 * A university load (DB_Master loader → AWS Postgres `technologies`) is
 * invisible to every layer that serves the corpus from its own copy:
 * Typesense, Pinecone, the Similar technologies lists, the public page URLs,
 * Algolia, the homepage counts document and the in-process filter lists /
 * search caches (Backend/DOCS/CORPUS_LOAD_RUNBOOK.md). This tab shows each
 * layer against Postgres, overall and per university, and starts the
 * catch-up as one background job on the API server.
 *
 * Backend: Backend/search_indexes.py through the dispatcher —
 *   GET  system/index_status  every layer vs Postgres (slow: 2–10 s, it asks
 *                             Typesense, Pinecone, Algolia and Postgres)
 *   GET  system/index_job     the job in this API process + the last 15 runs
 *   POST system/index_run     {steps} → starts the job; 400 text when one is
 *                             already running or the step list is bad
 *
 * The server runs the steps in the runbook's order whatever order they are
 * sent in, one job at a time. Nothing in it costs money: Pinecone vectors are
 * embedded on the server itself and the index writes are within plan.
 * Horizon (a paid LLM run) is only reported here — it starts from Horizon data.
 *
 * Note the server treats an EMPTY step list as "every step", so the Run
 * button refuses to send one rather than relying on the server's 400.
 */

const S = 'system';

// The backend's STEPS / STEP_LABELS, used until index_status answers (and if
// an older server sends no `steps`). The server's list wins when it has one.
const STEP_ORDER = ['typesense', 'pinecone', 'similar', 'paths', 'algolia', 'homepage', 'caches'];
const STEP_LABELS: Record<string, string> = {
  typesense: 'Typesense (quick search)',
  pinecone: 'Pinecone (AI search)',
  similar: 'Similar technologies',
  // Not in STEP_ORDER: a server that predates the onboarding picks step must
  // never be sent it. Its own status list carries it when it has it.
  onboarding: 'Onboarding picks',
  paths: 'Page URLs',
  algolia: 'Algolia (fallback search)',
  homepage: 'Homepage counts',
  caches: 'Search caches & filter lists',
  // Not in STEP_ORDER either (paid, opt-in): Backend/ocean/cosmos_match.py.
  ocean: 'OCEAN COSMOS re-score (paid LLM)',
};
/** One word per step, for the run summaries. */
const STEP_SHORT: Record<string, string> = {
  typesense: 'Typesense', pinecone: 'Pinecone', similar: 'Similar', onboarding: 'Picks', foryou: 'For You', ocean: 'OCEAN',
  paths: 'URLs', algolia: 'Algolia', homepage: 'Homepage', whatsnew: "What's New", caches: 'Caches',
};
/** Steps outside STEP_ORDER that a server may also have. */
const OPTIONAL_STEPS = ['onboarding', 'foryou', 'ocean', 'whatsnew'];
/** A run of every step: the fallback list, plus whichever optional steps the
 *  server has ("All 11" rather than eleven names). */
const ranEveryStep = (steps: string[]) =>
  STEP_ORDER.every(s => steps.includes(s)) && steps.every(s => STEP_ORDER.includes(s) || OPTIONAL_STEPS.includes(s));
const DISMISS_KEY = 'utech_admin_search_indexes_dismissed';
/** Steps that do the work of a group or sub-tab another admin may not have:
 *  [group] or [group, tab]. The server leaves them out of index_status for
 *  that admin and refuses them in index_run; this keeps them off the list. */
const GATED_STEPS: Record<string, [string, string?]> = {
  foryou: ['foryou_whatsnew'], whatsnew: ['foryou_whatsnew'], ocean: ['ocean', 'cosmos_matching'],
};

interface Layer {
  key: string; label: string; ok: boolean; error?: string | null;
  count: number | null; expected: number; missing: number | null; extra: number | null;
  up_to_date: boolean | null; note?: string; ms?: number | null;
}
interface UniRow {
  code: string; name: string; country: string; postgres: number;
  typesense: number | null; algolia: number | null;
  pinecone_spot?: { checked: number; present: number } | null;
  similar_missing: number | null; paths_missing: number | null;
  horizon_pending: number | null; label: string | null;
}
interface StepResult { status?: string; error?: string; seconds_total?: number; [k: string]: any }
interface Job {
  running: boolean; steps?: string[]; by?: string; started_at?: string; finished_at?: string | null;
  current?: string | null; results?: Record<string, StepResult>; log?: string[];
  status?: string; run_id?: number | null; error?: string | null;
}
interface Run {
  id: number; started_at: string | null; finished_at: string | null; started_by: string | null;
  steps: string[] | null; status: string | null; results: Record<string, StepResult> | null; error: string | null;
}
interface Status {
  postgres: { count: number; universities: number };
  layers: Layer[];
  universities: UniRow[];
  horizon: { pending: number | null; ok: boolean; error?: string | null };
  labels_missing: string[];
  details: Record<string, { ok: boolean; error?: string; ms?: number }>;
  similar?: { method?: string | null };
  pinecone?: { index?: string | null; namespace?: string | null };
  checked_at: string;
  steps?: { key: string; label: string }[];
  job?: Job;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
const isNum = (v: any): v is number => typeof v === 'number' && Number.isFinite(v);
const fmtN = (v: any): string => (isNum(v) ? v.toLocaleString() : '—');
const fmtSecs = (s: any): string => {
  if (!isNum(s)) return '—';
  if (s < 10) return `${s.toFixed(1)} s`;
  if (s < 60) return `${Math.round(s)} s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s - m * 60);
  return r ? `${m} min ${r} s` : `${m} min`;
};
const secsBetween = (a?: string | null, b?: string | null): number | null => {
  const t0 = Date.parse(a || ''), t1 = b ? Date.parse(b) : NaN;
  return Number.isFinite(t0) && Number.isFinite(t1) ? Math.max(0, (t1 - t0) / 1000) : null;
};
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

const STATUS_TONE: Record<string, Tone> = {
  ok: 'good', done: 'good', warning: 'warn', failed: 'bad', running: 'info',
  pending: 'neutral', 'not run': 'neutral', interrupted: 'bad',
};
const StatePill: React.FC<{ value: string; title?: string }> = ({ value, title }) => (
  <Pill tone={STATUS_TONE[value] || 'neutral'} title={title}>{value}</Pill>
);

/** The numbers worth reading for one finished step (all fields optional). */
const stepFacts = (step: string, r: StepResult): string[] => {
  const out: string[] = [];
  const add = (cond: any, text: string) => { if (cond) out.push(text); };
  if (step === 'typesense' || step === 'pinecone') {
    const unit = step === 'typesense' ? 'documents' : 'vectors';
    add(isNum(r.before) || isNum(r.after), `${fmtN(r.before)} → ${fmtN(r.after)} ${unit}`);
    add(step === 'pinecone' && isNum(r.missing), `${fmtN(r.missing)} missing`);
    add(isNum(r.upserted), step === 'pinecone' ? `${fmtN(r.upserted)} embedded and upserted` : `${fmtN(r.upserted)} upserted`);
    add(isNum(r.failed) && r.failed > 0, `${fmtN(r.failed)} failed`);
    add(isNum(r.stale) && r.stale > 0, `${fmtN(r.stale)} not in the database`);
    add(isNum(r.removed) && r.removed > 0, `${fmtN(r.removed)} removed`);
  } else if (step === 'similar') {
    add(isNum(r.techs), `${fmtN(r.techs)} technologies`);
    add(isNum(r.rows), `${fmtN(r.rows)} list rows`);
  } else if (step === 'paths') {
    add(isNum(r.inserted), `${fmtN(r.inserted)} new`);
    add(isNum(r.moved), `${fmtN(r.moved)} moved`);
    add(isNum(r.revived) && r.revived > 0, `${fmtN(r.revived)} revived`);
    add(isNum(r.retired), `${fmtN(r.retired)} retired`);
    add(isNum(r.unchanged), `${fmtN(r.unchanged)} unchanged`);
  } else if (step === 'algolia') {
    add(isNum(r.records), `${fmtN(r.records)} records uploaded`);
  } else if (step === 'homepage') {
    add(isNum(r.technologies), `${fmtN(r.technologies)} technologies`);
    add(isNum(r.universities), `${fmtN(r.universities)} universities`);
  } else if (step === 'ocean') {
    add(isNum(r.technologies), `${fmtN(r.technologies)} technologies in OCEAN's index`);
    if (r.refused) out.push(`would have removed ${fmtN(r.removed)} technologies — refused, the index is unchanged`);
    else add(isNum(r.added) || isNum(r.removed), `+${fmtN(r.added)} added, −${fmtN(r.removed)} removed, ${fmtN(r.edited)} edited`);
    // plural() needs a number, and add() builds its text before testing: these two test first.
    if (isNum(r.lost_a_match) && r.lost_a_match > 0) out.push(`${plural(r.lost_a_match, 'statement')} lost a match`);
    add(isNum(r.overlapping_new), `${fmtN(r.overlapping_new)} of ${fmtN(r.scored_statements)} scored statements share words with a new technology`);
    if (isNum(r.requeued)) {
      out.push(`${plural(r.requeued, 'statement')} re-queued${r.full ? ' (every scored statement)' : ''}`
        + (isNum(r.estimated_llm_calls) ? ` — ~${fmtN(r.estimated_llm_calls)} LLM calls` : '')
        + (isNum(r.days_at_cap) ? `, ~${plural(r.days_at_cap, 'day')} at the background budget` : ''));
    }
  } else if (step === 'caches') {
    const f = r.facets || {};
    add(isNum(f.technologies),
      `filter lists: ${fmtN(f.technologies)} technologies, ${fmtN(f.universities)} universities, ${fmtN(f.countries)} countries`);
    const x = r.extractor || {};
    add(isNum(x.universities),
      `AI filter extractor: ${fmtN(x.universities)} universities, ${fmtN(x.countries)} countries${x.kept_previous ? ' (previous list kept)' : ''}`);
    add(isNum(r.results_cleared), `${plural(r.results_cleared, 'cached search result')} cleared`);
  }
  return out;
};

/** Sentences a step hands back for a person to read (amber), separate from errors (red). */
const stepNotes = (r: StepResult): string[] => {
  const out: string[] = [];
  if (r.removal_skipped) out.push(String(r.removal_skipped));
  if (r.note) out.push(String(r.note));
  if (r.filter_words?.error) out.push(`Filter words: ${r.filter_words.error}`);
  if (r.nonsense?.error) out.push(`Nonsense word list: ${r.nonsense.error}`);
  if (Array.isArray(r.failures_sample) && r.failures_sample.length) {
    out.push(`Failed sample: ${r.failures_sample.map((x: any) => (typeof x === 'string' ? x : JSON.stringify(x))).join(' · ')}`);
  }
  return out;
};

/** "Typesense +773 · Pinecone failed" — one phrase per step, for the runs table. */
const stepShort = (step: string, r?: StepResult): string => {
  const name = STEP_SHORT[step] || step;
  if (!r) return `${name} not run`;
  if (r.status === 'failed') return `${name} failed`;
  const warn = r.status === 'warning' ? ' (warning)' : '';
  if (step === 'typesense' && isNum(r.before) && isNum(r.after)) {
    const d = r.after - r.before;
    return `${name} ${d >= 0 ? '+' : ''}${d.toLocaleString()}${warn}`;
  }
  if (step === 'pinecone' && isNum(r.upserted)) {
    return `${name} +${r.upserted.toLocaleString()}${isNum(r.removed) && r.removed ? ` −${r.removed.toLocaleString()}` : ''}${warn}`;
  }
  if (step === 'similar' && isNum(r.techs)) return `${name} ${r.techs.toLocaleString()}${warn}`;
  if (step === 'paths' && isNum(r.inserted)) return `${name} +${r.inserted.toLocaleString()}${warn}`;
  if (step === 'algolia' && isNum(r.records)) return `${name} ${r.records.toLocaleString()}${warn}`;
  if (step === 'homepage' && isNum(r.technologies)) return `${name} ${r.technologies.toLocaleString()}${warn}`;
  if (step === 'whatsnew' && isNum(r.added) && isNum(r.removed)) return `${name} +${r.added.toLocaleString()} −${r.removed.toLocaleString()}${warn}`;
  if (step === 'ocean' && r.refused) return `${name} refused (warning)`;
  if (step === 'ocean' && isNum(r.added) && isNum(r.removed)) {
    return `${name} +${r.added.toLocaleString()} −${r.removed.toLocaleString()}`
      + (isNum(r.requeued) ? ` · ${r.requeued.toLocaleString()} re-queued` : '') + warn;
  }
  return `${name} ${r.status === 'warning' ? 'warning' : 'ok'}`;
};

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------
/** The kit's KPI look, with a hint that may hold a link. */
const LinkKPI: React.FC<{ label: string; value: React.ReactNode; hint?: React.ReactNode }> =
  ({ label, value, hint }) => (
  <div className="rounded-xl border px-4 py-3 min-w-[120px] flex-1 border-grayscale-300 bg-white">
    <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">{label}</div>
    <div className="text-xl font-bold text-grayscale-1300 font-grotesk">{value}</div>
    {hint && <div className="text-[11px] text-grayscale-1100">{hint}</div>}
  </div>
);

type Mark = 'amber' | 'red' | null;
const MARK: Record<'amber' | 'red', string> = {
  amber: 'bg-amber-50 text-amber-800',
  red: 'bg-red-50 text-red-700',
};
/** A number, tinted when it disagrees with the database. */
const Num: React.FC<{ v: any; mark?: Mark; title?: string }> = ({ v, mark, title }) => (
  mark
    ? <span title={title} className={`inline-block rounded px-1.5 -mx-1.5 font-medium ${MARK[mark]}`}>{fmtN(v)}</span>
    : <span title={title}>{fmtN(v)}</span>
);
/** A layer's count of one university against its database count. */
const countMark = (v: number | null, pg: number): Mark =>
  !isNum(v) || v === pg ? null : v === 0 && pg > 0 ? 'red' : 'amber';
/** A "missing" count: red when the university has none at all. */
const missingMark = (v: number | null, pg: number): Mark =>
  !isNum(v) || v <= 0 ? null : v >= pg ? 'red' : 'amber';

const LayerStatus: React.FC<{ l: Layer }> = ({ l }) => {
  if (!l.ok || l.error) {
    return (
      <span className="text-red-600 text-xs whitespace-normal break-words line-clamp-3 max-w-[22rem]"
        title={l.error || undefined}>
        {l.error || 'Could not check'}
      </span>
    );
  }
  if (l.up_to_date === true) return <Pill tone="good">Up to date</Pill>;
  if (l.up_to_date === false) {
    const parts: string[] = [];
    if (isNum(l.missing) && l.missing > 0) parts.push(`${l.missing.toLocaleString()} behind`);
    if (isNum(l.extra) && l.extra > 0) parts.push(`${l.extra.toLocaleString()} extra`);
    return <Pill tone="warn">{parts.join(' · ') || 'Out of date'}</Pill>;
  }
  return <span className="text-grayscale-1100">—</span>;
};

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------
const SearchIndexesPanel: React.FC = () => {
  const status = useAdminGet<Status>(S, 'index_status');
  const st = status.data;

  // The job and the run history come from index_job; the status call's own
  // `job` is only used to notice a run already in progress on arrival.
  const [job, setJob] = useState<Job | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [jobErr, setJobErr] = useState('');
  const [jobLoaded, setJobLoaded] = useState(false);
  const polling = useRef(false);
  const loadJob = useCallback(async () => {
    if (polling.current) return;               // a slow answer must not stack requests
    polling.current = true;
    const { data, error } = await panelGet(S, 'index_job');
    polling.current = false;
    setJobLoaded(true);
    setJobErr(error);
    if (data) {
      setJob((data as any).job || { running: false });
      setRuns(Array.isArray((data as any).runs) ? (data as any).runs : []);
    }
  }, []);
  useEffect(() => { loadJob(); }, [loadJob]);
  useEffect(() => {
    const sj = st?.job;
    if (sj && (!job || (sj.running && !job.running))) setJob(sj);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st]);

  const running = !!job?.running;
  // Every 3 s while a run is in flight.
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(loadJob, 3000);
    return () => window.clearInterval(t);
  }, [running, loadJob]);
  // When it ends, the layers have moved: check them once more.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running) status.reload();
    wasRunning.current = running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  // A finished run stays on screen until dismissed (remembered per browser,
  // by its start time, so the next run shows again).
  const [dismissed, setDismissed] = useState<string>(() => {
    try { return localStorage.getItem(DISMISS_KEY) || ''; } catch { return ''; }
  });
  const dismiss = () => {
    const k = job?.started_at || '';
    setDismissed(k);
    try { localStorage.setItem(DISMISS_KEY, k); } catch { /* private mode */ }
  };
  const showJob = !!job?.steps?.length && (running || (job?.started_at || '') !== dismissed);

  // Steps and their labels: the server's list once it has answered, without
  // the steps of groups / sub-tabs this admin cannot open.
  const access = useAdminAccess();
  const steps = useMemo(() => (st?.steps?.length ? st.steps.map(s => s.key) : STEP_ORDER).filter(k => {
    const g = GATED_STEPS[k];
    return !g || (g[1] ? access.canTab(g[0], g[1]) : access.canGroup(g[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [st, access.legacy, access.superAdmin, access.groups, access.tabs]);
  const labelOf = useCallback((k: string) =>
    st?.steps?.find(s => s.key === k)?.label || STEP_LABELS[k] || k, [st]);

  // Selection for the next run: every step by default — the safe full catch-up.
  const [picked, setPicked] = useState<string[] | null>(null);
  const selected = picked ?? steps;
  const ordered = steps.filter(s => selected.includes(s));
  const toggle = (k: string, on: boolean) =>
    setPicked(on ? steps.filter(s => s === k || selected.includes(s)) : selected.filter(s => s !== k));
  const layers: Layer[] = st?.layers || [];
  const behind = layers.filter(l => l.up_to_date === false);
  const unchecked = layers.filter(l => !l.ok || l.error);
  const pickBehind = () => setPicked(steps.filter(s => s === 'caches' || behind.some(l => l.key === s)));

  const [confirm, setConfirm] = useState(false);
  const [starting, setStarting] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'error' | 'ok'; text: string } | null>(null);
  const start = async () => {
    if (!ordered.length) return;
    setStarting(true); setMsg(null);
    const { data, error } = await panelPost(S, 'index_run', { steps: ordered });
    setStarting(false);
    setConfirm(false);
    if (error) {
      setMsg({ kind: 'error', text: error });
      loadJob();                                // most likely: a run is already going — show it
      return;
    }
    setJob((data as any)?.job || { running: true, steps: ordered });
    loadJob();
  };

  // The run needs only the step list, never index_status — so these controls
  // also stand alone when that (slow, four-system) check fails.
  const runControls = (withBehind: boolean) => (
    <div className="flex items-center gap-2 flex-wrap">
      <Btn small onClick={() => setPicked(null)} title="Every step — the safe full catch-up">All</Btn>
      {withBehind && (
        <Btn small onClick={pickBehind}
          title="The layers behind the database, plus the caches (always worth dropping after a load)">
          Only what's behind
        </Btn>
      )}
      <Btn kind="primary" small onClick={() => { setMsg(null); setConfirm(true); }}
        disabled={!ordered.length || running || starting}
        title={running ? 'A run is in progress — wait for it to finish'
          : !ordered.length ? 'Tick at least one layer' : undefined}>
        Run selected ({ordered.length})
      </Btn>
    </div>
  );
  const allBox = (
    <input type="checkbox" aria-label="Select every layer" className="accent-primary-500"
      checked={ordered.length === steps.length}
      ref={el => { if (el) el.indeterminate = ordered.length > 0 && ordered.length < steps.length; }}
      onChange={e => setPicked(e.target.checked ? null : [])} />
  );

  const firstLoad = status.loading && !st;
  const pg = st?.postgres;
  const horizon = st?.horizon;
  const labelsOk = st?.details?.labels?.ok !== false;
  // Horizon's own failure shows in its KPI; these two have no other place.
  const otherFailures = ['labels', 'caches']
    .filter(k => st?.details?.[k] && st.details[k].ok === false)
    .map(k => `${k}: ${st!.details[k].error || 'failed'}`);

  return (
    <div className="flex flex-col gap-4">
      <Section title="Search indexes"
        right={
          <div className="flex items-center gap-2">
            {st?.checked_at && (
              <span className="text-[11px] text-grayscale-1100">
                {status.loading ? 'Checking every layer… (up to 10 s)' : `Checked ${fmtDate(st.checked_at)}`}
              </span>
            )}
            <Btn small onClick={status.reload} disabled={status.loading}
              title="Ask every layer again (slow: 2–10 s)">
              {status.loading ? 'Checking…' : 'Check again'}
            </Btn>
          </div>
        }>
        <p className="text-xs text-grayscale-1100 -mt-1 max-w-3xl">
          After new universities are loaded into the database, the search layers below must catch up — each one
          serves the technologies from its own copy. Every row shows what the layer holds against the database;
          tick the layers to bring up to date and press <b>Run selected</b>.
        </p>
        {status.error && <Msg kind="error">Could not check the layers: {status.error}</Msg>}
        {!!otherFailures.length && (
          <Msg kind="error">Could not check {otherFailures.join(' · ')}</Msg>
        )}
      </Section>

      {firstLoad ? (
        <Loading label="Asking Typesense, Pinecone, Algolia and the database… (up to 10 s)" />
      ) : !st ? (
        // ---- Layers, when the check failed: the bare step list, no counts --
        <Section title="Layers" right={runControls(false)}>
          {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
          <p className="text-xs text-grayscale-1100 -mt-1 max-w-3xl">
            Without the check there are no counts to show, but the catch-up does not need them: tick the steps
            and press <b>Run selected</b>. <b>All</b> is the safe full catch-up.
          </p>
          <div className="overflow-auto rounded-lg border border-grayscale-300">
            <table className="w-full text-sm">
              <thead className="bg-grayscale-200">
                <tr>
                  <th className="px-3 py-2 w-8">{allBox}</th>
                  <th className="font-grotesk text-xs px-3 py-2 whitespace-nowrap text-grayscale-1200 text-left">
                    Layer
                  </th>
                </tr>
              </thead>
              <tbody>
                {steps.map(k => (
                  <tr key={k} className="border-t border-grayscale-200 hover:bg-grayscale-100">
                    <td className="px-3 py-2">
                      <input type="checkbox" className="accent-primary-500" aria-label={`Include ${labelOf(k)}`}
                        checked={selected.includes(k)} onChange={e => toggle(k, e.target.checked)} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-grayscale-1300 font-medium">{labelOf(k)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ) : (
        <>
          <KPIRow>
            <KPI label="Technologies in the database" value={fmtN(pg?.count)}
              hint={isNum(pg?.universities) ? `${plural(pg!.universities, 'university', 'universities')}` : undefined} />
            <KPI label="Layers behind"
              value={<span className={behind.length ? 'text-amber-700' : undefined}>{behind.length}</span>}
              hint={behind.length
                ? behind.map(l => STEP_SHORT[l.key] || l.label).join(', ')
                  + (unchecked.length ? ` · ${unchecked.length} could not be checked` : '')
                : unchecked.length ? `${unchecked.length} could not be checked` : `all ${layers.length} up to date`} />
            <LinkKPI label="Horizon pending"
              value={horizon?.ok === false ? '—' : fmtN(horizon?.pending)}
              hint={horizon?.ok === false
                ? <span className="text-red-600">{horizon.error || 'Could not check'}</span>
                : <>A paid AI run, never part of this job ·{' '}
                    <AdminLink to={{ group: 'horizon', tab: 'pending' }}>Horizon data → Pending</AdminLink></>} />
            <LinkKPI label="Universities without a website label"
              value={labelsOk ? (st.labels_missing || []).length : '—'}
              hint={<>
                {labelsOk && !!st.labels_missing?.length && (
                  <span className="font-mono" title={st.labels_missing.join(', ')}>
                    {st.labels_missing.slice(0, 3).join(', ')}{st.labels_missing.length > 3 ? ' …' : ''} ·{' '}
                  </span>
                )}
                <AdminLink to={{ group: 'universities' }}>Set it in Universities</AdminLink>
              </>} />
          </KPIRow>

          {/* ---- Layers ---------------------------------------------------- */}
          <Section title="Layers" right={runControls(true)}>
            {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}
            <div className="overflow-auto rounded-lg border border-grayscale-300">
              <table className="w-full text-sm">
                <thead className="bg-grayscale-200">
                  <tr>
                    <th className="px-3 py-2 w-8">{allBox}</th>
                    {['Layer', 'In layer', 'Database', 'Missing', 'Extra', 'Status', 'Note'].map((h, i) => (
                      <th key={h}
                        className={`font-grotesk text-xs px-3 py-2 whitespace-nowrap text-grayscale-1200
                          ${i >= 1 && i <= 4 ? 'text-right' : 'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {layers.map(l => {
                    const on = selected.includes(l.key);
                    const runnable = steps.includes(l.key);
                    return (
                      <tr key={l.key} className="border-t border-grayscale-200 hover:bg-grayscale-100 align-top">
                        <td className="px-3 py-2">
                          <input type="checkbox" className="accent-primary-500" aria-label={`Include ${l.label}`}
                            checked={on} disabled={!runnable} onChange={e => toggle(l.key, e.target.checked)} />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-grayscale-1300 font-medium"
                          title={isNum(l.ms) ? `Checked in ${fmtSecs(l.ms / 1000)}` : undefined}>
                          {l.label}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtN(l.count)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmtN(l.expected)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <Num v={l.missing} mark={isNum(l.missing) && l.missing > 0 ? 'amber' : null} />
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          <Num v={l.extra} mark={isNum(l.extra) && l.extra > 0 ? 'amber' : null} />
                        </td>
                        <td className="px-3 py-2"><LayerStatus l={l} /></td>
                        <td className="px-3 py-2 text-xs text-grayscale-1100 min-w-[14rem]">{l.note || ''}</td>
                      </tr>
                    );
                  })}
                  {!layers.length && (
                    <tr><td colSpan={8} className="px-3 py-4 text-center text-sm text-grayscale-1100">
                      The server reported no layers.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <p className="text-[11px] text-grayscale-1100">
              Missing and Extra compare the layer's count with the database; Similar technologies and Page URLs are
              checked technology by technology.
              {st.pinecone?.index && <> Pinecone index <span className="font-mono">{st.pinecone.index}</span>
                {st.pinecone.namespace ? <> / <span className="font-mono">{st.pinecone.namespace}</span></> : null}.</>}
            </p>
          </Section>
        </>
      )}

      {/* ---- The run in progress / the last one ------------------------------ */}
      {showJob && job && <JobCard job={job} labelOf={labelOf} onDismiss={dismiss} />}

      {/* ---- Per university -------------------------------------------------- */}
      {st && <Universities rows={st.universities || []} />}

      {/* ---- History --------------------------------------------------------- */}
      <RecentRuns runs={runs} job={job} error={jobErr} loading={!jobLoaded} onRefresh={loadJob} />

      {confirm && (
        <Modal title="Bring these layers up to date?" onClose={() => !starting && setConfirm(false)}
          footer={<>
            <Btn onClick={() => setConfirm(false)} disabled={starting}>Cancel</Btn>
            <Btn kind="primary" onClick={start} disabled={starting || !ordered.length}>
              {starting ? 'Starting…' : `Run ${plural(ordered.length, 'step')}`}
            </Btn>
          </>}>
          <ol className="list-decimal pl-5 flex flex-col gap-0.5">
            {ordered.map(k => <li key={k}>{labelOf(k)}</li>)}
          </ol>
          <p className="text-xs text-grayscale-1100">
            Runs on the server in the background, in this order, and takes a few minutes; you can leave this page.
            No AI cost: the missing Pinecone vectors are embedded on the server itself. Live search keeps working
            throughout — Typesense is upserted, never dropped.
          </p>
          <p className="text-xs text-grayscale-1100">
            Entries no longer in the database are removed from Typesense and Pinecone, unless that would touch more
            than 2% of an index — then nothing is removed and the run says why. Horizon is not part of this job.
          </p>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// The run in progress (or the last one, until dismissed)
// ---------------------------------------------------------------------------
const JobCard: React.FC<{ job: Job; labelOf: (k: string) => string; onDismiss: () => void }> =
  ({ job, labelOf, onDismiss }) => {
  const results = job.results || {};
  const log = job.log || [];
  const end = job.finished_at || (job.running ? new Date().toISOString() : null);
  const took = secsBetween(job.started_at, end);

  // Follow the log as it grows, unless someone scrolled up to read it.
  const box = useRef<HTMLPreElement | null>(null);
  const stick = useRef(true);
  useEffect(() => {
    const el = box.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [log.length, log[log.length - 1]]);

  const stateOf = (k: string): string => {
    const r = results[k];
    if (r?.status) return r.status;
    if (job.current === k && job.running) return 'running';
    return job.running ? 'pending' : 'not run';
  };
  const overall = job.status || (job.running ? 'running' : 'done');

  return (
    <Section title={job.running ? 'Catch-up running' : 'Last catch-up'}
      right={
        <div className="flex items-center gap-2">
          <StatePill value={overall} />
          {!job.running && <Btn small onClick={onDismiss} title="Hide this until the next run">Dismiss</Btn>}
        </div>
      }>
      <div className="text-xs text-grayscale-1100 -mt-1">
        Started by {job.by || 'an admin'} · {fmtDate(job.started_at)}
        {job.finished_at ? <> · finished {fmtDate(job.finished_at)}</> : null}
        {isNum(took) ? <> · {job.running ? 'running for' : 'took'} {fmtSecs(took)}</> : null}
        {job.running && <> · refreshes every 3 s</>}
      </div>
      {job.running && (
        <div className="text-sm text-grayscale-1300">
          Now: <b>{job.current ? labelOf(job.current) : 'starting…'}</b>
        </div>
      )}
      {job.error && <Msg kind="error">{job.error}</Msg>}
      <ol className="flex flex-col divide-y divide-grayscale-200 rounded-lg border border-grayscale-300">
        {(job.steps || []).map((k, i) => {
          const r = results[k];
          const state = stateOf(k);
          const facts = r ? stepFacts(k, r) : [];
          const notes = r ? stepNotes(r) : [];
          return (
            <li key={k} className="px-3 py-2 flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] text-grayscale-1100 w-4 text-right">{i + 1}</span>
                <StatePill value={state} />
                <span className={`text-sm ${state === 'pending' ? 'text-grayscale-1100' : 'text-grayscale-1300 font-medium'}`}>
                  {labelOf(k)}
                </span>
                {r && isNum(r.seconds_total) && (
                  <span className="text-[11px] text-grayscale-1100">· {fmtSecs(r.seconds_total)}</span>
                )}
              </div>
              {!!facts.length && (
                <div className="text-xs text-grayscale-1200 pl-6">{facts.join(' · ')}</div>
              )}
              {r?.error && <div className="text-xs text-red-600 pl-6 break-words">{r.error}</div>}
              {notes.map((n, j) => (
                <div key={j} className="text-xs text-amber-700 pl-6 break-words">{n}</div>
              ))}
            </li>
          );
        })}
      </ol>
      <div className="flex flex-col gap-1">
        <span className="text-[11px] text-grayscale-1100">Server log, UTC (last {plural(log.length, 'line')})</span>
        <pre ref={box}
          onScroll={e => {
            const el = e.currentTarget;
            stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
          }}
          className="max-h-64 overflow-auto rounded-lg border border-grayscale-300 bg-grayscale-200 px-3 py-2
            text-[11px] leading-relaxed font-mono text-grayscale-1200 whitespace-pre-wrap break-words">
          {log.length ? log.join('\n') : 'No output yet.'}
        </pre>
      </div>
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Per university
// ---------------------------------------------------------------------------
const UNI_COLS = ['university', 'country', 'postgres', 'typesense', 'algolia', 'pinecone',
  'similar_missing', 'paths_missing', 'horizon_pending', 'label'];
const UNI_LABELS: Record<string, string> = {
  university: 'University', country: 'Country', postgres: 'Database', typesense: 'Typesense',
  algolia: 'Algolia', pinecone: 'Pinecone', similar_missing: 'Similar missing',
  paths_missing: 'Page URL missing', horizon_pending: 'Horizon pending', label: 'Website label',
};

/** Whether a university differs from the database in any column. */
const hasGap = (u: UniRow): boolean =>
  (isNum(u.typesense) && u.typesense !== u.postgres)
  || (isNum(u.algolia) && u.algolia !== u.postgres)
  || (!!u.pinecone_spot && u.pinecone_spot.present !== u.pinecone_spot.checked)
  || (isNum(u.similar_missing) && u.similar_missing > 0)
  || (isNum(u.paths_missing) && u.paths_missing > 0)
  || (isNum(u.horizon_pending) && u.horizon_pending > 0);

const Universities: React.FC<{ rows: UniRow[] }> = ({ rows }) => {
  const gaps = useMemo(() => rows.filter(hasGap).length, [rows]);
  // Follows the data ("on when any row has a gap") until someone flips it.
  const [only, setOnly] = useState<boolean | null>(null);
  const onlyGaps = only ?? gaps > 0;

  const view = useMemo(() => rows
    .filter(u => !onlyGaps || hasGap(u))
    .map(u => ({
      ...u,
      // Name and code in one searchable, sortable value.
      university: `${u.name || u.code} · ${u.code}`,
      pinecone: !u.pinecone_spot ? null : u.pinecone_spot.present === u.pinecone_spot.checked ? '✓' : 'missing',
      label: u.label || null,
    })), [rows, onlyGaps]);

  return (
    <Section title="Per university"
      right={
        <label className="flex items-center gap-1.5 text-xs text-grayscale-1200 cursor-pointer">
          <input type="checkbox" className="accent-primary-500" checked={onlyGaps}
            onChange={e => setOnly(e.target.checked)} />
          Only universities with gaps ({gaps.toLocaleString()} of {rows.length.toLocaleString()})
        </label>
      }>
      <p className="text-xs text-grayscale-1100 -mt-1 max-w-3xl">
        Each university's technologies in every layer. Amber: differs from the database; red: the layer has none
        of them. Pinecone is a spot check of each university's first and newest technology (the run compares every
        one). Horizon pending is filled from Horizon data, not by this job.
      </p>
      <DataTable rows={view} columns={UNI_COLS} labels={UNI_LABELS} searchable csvName="search_index_universities"
        tableId="search_index_universities" maxHeight="34rem"
        empty={onlyGaps ? 'Every university is in every layer.' : 'No universities reported.'}
        render={{
          university: r => (
            <span className="flex flex-col leading-tight">
              <span className="text-grayscale-1300">{r.name || r.code}</span>
              <span className="text-[11px] text-grayscale-1100 font-mono">{r.code}</span>
            </span>
          ),
          postgres: r => <Num v={r.postgres} />,
          typesense: r => <Num v={r.typesense} mark={countMark(r.typesense, r.postgres)} />,
          algolia: r => <Num v={r.algolia} mark={countMark(r.algolia, r.postgres)} />,
          pinecone: r => {
            const s = r.pinecone_spot;
            if (!s) return <span className="text-grayscale-1100">—</span>;
            const tip = `${s.present} of ${s.checked} spot-checked technologies found`;
            if (s.present === s.checked) return <span className="text-green-700" title={tip}>✓</span>;
            return (
              <span title={tip} className={`inline-block rounded px-1.5 -mx-1.5 font-medium ${MARK[s.present ? 'amber' : 'red']}`}>
                missing
              </span>
            );
          },
          similar_missing: r => <Num v={r.similar_missing} mark={missingMark(r.similar_missing, r.postgres)} />,
          paths_missing: r => <Num v={r.paths_missing} mark={missingMark(r.paths_missing, r.postgres)} />,
          horizon_pending: r => (isNum(r.horizon_pending) && r.horizon_pending > 0
            ? (
              <AdminLink to={{ group: 'horizon', tab: 'pending', university: r.code }}
                title="Open Horizon data → Pending for this university"
                className={`inline-block rounded px-1.5 -mx-1.5 font-medium underline underline-offset-2
                  ${MARK[missingMark(r.horizon_pending, r.postgres) || 'amber']}`}>
                {fmtN(r.horizon_pending)}
              </AdminLink>
            )
            : <Num v={r.horizon_pending} />),
          label: r => (r.label
            ? <span>{r.label}</span>
            : <span className="text-amber-700" title="No website label — set it in Universities">—</span>),
        }} />
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Recent runs (search_index_runs, newest first)
// ---------------------------------------------------------------------------
const RecentRuns: React.FC<{
  runs: Run[]; job: Job | null; error: string; loading: boolean; onRefresh: () => void;
}> = ({ runs, job, error, loading, onRefresh }) => {
  const rows = useMemo(() => {
    // A row still marked running that is not this process's live job was cut
    // off by an API restart: it will never finish.
    const live = job?.running
      ? (job.run_id ?? runs.find(r => r.status === 'running')?.id ?? null)
      : null;
    return runs.map(r => {
      const status = r.status === 'running' && r.id !== live ? 'interrupted' : (r.status || '—');
      const steps = Array.isArray(r.steps) ? r.steps : [];
      const res = r.results || {};
      return {
        started_at: r.started_at,
        started_by: r.started_by || '—',
        steps: ranEveryStep(steps)
          ? `All ${steps.length}` : steps.map(s => STEP_SHORT[s] || s).join(', '),
        status,
        duration_s: secsBetween(r.started_at, r.finished_at),
        summary: r.error
          ? r.error
          : steps.filter(s => res[s]).map(s => stepShort(s, res[s])).join(' · '),
        _id: r.id,
      };
    });
  }, [runs, job]);

  return (
    <Section title="Recent runs" right={<Btn small onClick={onRefresh}>Refresh</Btn>}>
      {error && <Msg kind="error">{error}</Msg>}
      {loading ? <Loading /> : (
        <DataTable rows={rows} columns={['started_at', 'started_by', 'steps', 'status', 'duration_s', 'summary']}
          labels={{ started_at: 'Started', started_by: 'By', steps: 'Steps', status: 'Status',
            duration_s: 'Duration', summary: 'Summary' }}
          tableId="search_index_runs" maxHeight="24rem"
          empty="No catch-up has been run yet."
          render={{
            status: r => (
              <StatePill value={r.status}
                title={r.status === 'interrupted'
                  ? 'The API restarted while this run was in progress; it did not finish.' : undefined} />
            ),
            duration_s: r => <span>{fmtSecs(r.duration_s)}</span>,
          }} />
      )}
    </Section>
  );
};

export default SearchIndexesPanel;
